import { ReactNode } from 'react'

import { AstraChip, AstraParagraph, ColumnSize, DataTable, RowType } from '~/astra'
import { ChipEllipsis } from '~/components'
import { EquipmentConstraint } from '~/utils/hospitalConstraints'

type EquipmentConstraintColOption = 'Navn på utstyr' | 'DIPS operasjonstyper' | 'Maks per dag'
type ColumnProps = { id: EquipmentConstraintColOption } & ColumnSize

const cols: ColumnProps[] = [
    {
        id: 'Navn på utstyr',
        maxWidth: '350px',
    },
    { id: 'DIPS operasjonstyper' },
    { id: 'Maks per dag', maxWidth: '150px' },
]

const getHeaders = (): RowType<ColumnProps>[] => {
    const tempArr: RowType<ColumnProps>[] = [
        {
            key: 'header',
            cellClassName: () => `flex w-full border-subtle bg-surface-strong p-3`,
            cellRender: ({ id }) => (
                <AstraParagraph weight="medium" variant="body-small" className="text-default">
                    {id}
                </AstraParagraph>
            ),
        },
    ]

    return tempArr
}

const TextCell = ({ children }: { children: ReactNode }) => (
    <AstraParagraph variant="body" className="shrink text-default">
        {children}
    </AstraParagraph>
)

const SurgeryTypeCell = ({ types }: { types: EquipmentConstraint['hospital_surgery_types'] }) => {
    if (types.length === 0) {
        return (
            <AstraParagraph variant="body" className="shrink p-3 italic text-subtle">
                Ingen operasjonstyper
            </AstraParagraph>
        )
    }

    return (
        <div className="flex h-full w-full items-center">
            <ChipEllipsis>
                {types.map(g => (
                    <AstraChip key={g.id} variant="filled" label={g.name} />
                ))}
            </ChipEllipsis>
        </div>
    )
}

const Cell = ({ column, constraint }: { column: ColumnProps; constraint: EquipmentConstraint }) => {
    const cell: Record<EquipmentConstraintColOption, ReactNode> = {
        'Navn på utstyr': <TextCell>{constraint.name}</TextCell>,
        'DIPS operasjonstyper': <SurgeryTypeCell types={constraint.hospital_surgery_types} />,
        'Maks per dag': <TextCell>{typeof constraint.capacity === 'number' ? constraint.capacity : '-'}</TextCell>,
    }

    return cell[column.id]
}

type Props = {
    constraints: EquipmentConstraint[]
    isLoading: boolean
}

export const Table = ({ constraints, isLoading }: Props) => (
    <DataTable
        cols={cols}
        rows={constraints.map(constraint => ({ id: constraint.code, ...constraint }))}
        fullWidthCells
        headers={getHeaders()}
        isLoading={isLoading}
        optimizeRendering={{ cellHeight: 'tall' }}
        className="border-spacing-0"
        body={{
            cellClassName: () => `h-full w-full flex items-center align-center border-none !cursor-default !select-text pl-3`,
            cellRender: (constraint, column) => <Cell column={column} constraint={constraint} />,
        }}
    />
)
